/**
 * Utilidades de presentación de la bandeja de correos del proyecto.
 *
 * Nada de esto toca la API: son transformaciones de lo que ya vino archivado,
 * para que un hilo se lea como se lee en Gmail y no como un volcado de texto.
 */

/** "Diego Hanza <diego@x.com>" → "Diego Hanza". Sin nombre, el usuario del correo. */
export function nombreDe(de: string): string {
  if (!de) return '(desconocido)'
  const conNombre = de.match(/^\s*"?([^"<]+?)"?\s*</)
  const etiqueta = conNombre?.[1]?.trim()
  // Muchos clientes repiten la direccion como nombre para mostrar
  // (`"x@y.com" <x@y.com>`): mostrarla entera no aporta y come el ancho.
  if (etiqueta && !etiqueta.includes('@')) return etiqueta
  const email = emailDe(de)
  return email ? email.split('@')[0] : de.trim()
}

export function emailDe(de: string): string {
  return (de?.match(/[\w.+-]+@[\w.-]+\.\w+/)?.[0] || '').toLowerCase()
}

export function inicialDe(de: string): string {
  const n = nombreDe(de).trim()
  return n ? n[0].toUpperCase() : '?'
}

/**
 * Color estable del avatar: sale de un hash de la dirección, así la misma
 * persona se ve siempre igual aunque cambie de posición en la lista.
 */
export function colorAvatar(de: string): string {
  const clave = emailDe(de) || nombreDe(de)
  let h = 0
  for (let i = 0; i < clave.length; i++) h = (h * 31 + clave.charCodeAt(i)) >>> 0
  return `var(--avatar-${(h % 6) + 1})`
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

/**
 * Fecha como la muestra una bandeja: la hora si es de hoy, el día y el mes si
 * es de este año, y solo el año si es más viejo. La fecha completa no aporta
 * nada en una lista y le roba el ancho al asunto.
 */
export function fechaCorta(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const hoy = new Date()
  const mismoDia =
    d.getDate() === hoy.getDate() && d.getMonth() === hoy.getMonth() && d.getFullYear() === hoy.getFullYear()
  if (mismoDia) return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  if (d.getFullYear() === hoy.getFullYear()) return `${d.getDate()} ${MESES[d.getMonth()]}`
  return String(d.getFullYear())
}

/** Fecha completa, para el encabezado de un mensaje abierto. */
export function fechaLarga(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('es-PE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Separa el texto nuevo de la conversación citada debajo.
 *
 * Cada respuesta arrastra el hilo entero citado, así que sin esto un hilo de
 * doce mensajes repite doce veces lo mismo y no hay forma de leerlo. Se corta
 * en el primer marcador de cita: las líneas con `>`, el "El <fecha>, <alguien>
 * escribió:" que mete Gmail, el encabezado de Outlook y los separadores de
 * mensaje original.
 */
export function partirCita(cuerpo: string): { texto: string; cita: string } {
  if (!cuerpo) return { texto: '', cita: '' }
  const lineas = cuerpo.split('\n')

  let corte = -1
  for (let i = 0; i < lineas.length; i++) {
    const l = lineas[i].trim()
    if (!l) continue
    const esCita =
      l.startsWith('>') ||
      /(escribi[óo]|wrote|a \u00e9crit|sent):?\s*$/i.test(l) ||
      /^-{2,}\s*(mensaje original|forwarded message|original message)/i.test(l) ||
      /^_{5,}$/.test(l) ||
      /^(de|from)\s*:\s*.+<?[\w.+-]+@/i.test(l)
    if (esCita) { corte = i; break }
  }

  if (corte < 0) return { texto: cuerpo.trim(), cita: '' }

  const texto = lineas.slice(0, corte).join('\n').trim()
  const cita = lineas.slice(corte).join('\n').trim()
  // Un mensaje que es solo cita se deja entero: plegarlo lo dejaría vacío.
  if (!texto) return { texto: cita, cita: '' }
  return { texto, cita }
}

/** Primera línea útil, para el resumen de un mensaje colapsado. */
export function primeraLinea(cuerpo: string, max = 140): string {
  const { texto } = partirCita(cuerpo)
  const plano = texto.replace(/\s+/g, ' ').trim()
  return plano.length > max ? plano.slice(0, max) + '…' : plano
}

/**
 * Los participantes que importan: los de afuera.
 *
 * En un hilo archivado siempre estamos nosotros, así que mostrar la lista
 * completa gasta el ancho en repetir nuestra propia dirección. Se descartan las
 * del mismo dominio que el usuario conectado — el dominio sale de la sesión, no
 * va escrito en el código. Si no queda nadie (un hilo interno), se muestran
 * todos: es mejor eso que una fila sin remitente.
 */
export function participantesExternos(participantes: string[], miEmail?: string | null): string[] {
  const lista = participantes || []
  const miDominio = (miEmail || '').split('@')[1]?.toLowerCase()
  if (!miDominio) return lista
  const otros = lista.filter((p) => p.split('@')[1]?.toLowerCase() !== miDominio)
  return otros.length ? otros : lista
}

/**
 * Con quién es el hilo, para encabezar la fila.
 *
 * El `From` del último mensaje trae el nombre para mostrar, que es lo que se
 * quiere leer; pero si ese último mensaje es nuestro, nombrarnos a nosotros no
 * dice nada, y se cae a los participantes externos.
 */
export function etiquetaHilo(
  participantes: string[],
  deUltimo?: string | null,
  miEmail?: string | null,
): string {
  const externos = participantesExternos(participantes, miEmail)
  const miDominio = (miEmail || '').split('@')[1]?.toLowerCase()
  const emailUltimo = emailDe(deUltimo || '')
  const ultimoEsExterno = !!emailUltimo && (!miDominio || emailUltimo.split('@')[1] !== miDominio)

  if (ultimoEsExterno) {
    const resto = externos.filter((e) => e !== emailUltimo)
    const nombre = nombreDe(deUltimo || '')
    return resto.length ? `${nombre}, +${resto.length}` : nombre
  }
  return externos.map(nombreDe).join(', ') || '(sin remitente)'
}
